
















































































import { onMounted, defineComponent, ref } from '@vue/composition-api';
import { ModalSize, ButtonType, ParagraphSize } from '@getaccept/lib-ui';
import { EnumDocumentType } from '@getaccept/lib-shared/src/enums/DocumentType';
import type { SortOrder } from '@getaccept/lib-shared/src/enums/sort-order';
import SearchContent from '@getaccept/lib-shared/src/components/SearchContent.vue';
import { DsrDirectoryConstants } from '@getaccept/lib-shared/src/library/constants/dsr-directory.constants';
import DsrTemplatePickerConstants from '@getaccept/lib-shared/src/dsr-template-picker/constants/dsr-template-picker.constants';
import type { DsrSortAttribute } from '../api/dsr/types/enums/dsr-sort-attribute';
import { DsrService } from '../api/dsr/services/dsr.service';
import type {
  CreateRoomFromTemplatePayload,
  CreateRoomPayload,
} from '../api/dsr/types/create-room-payload';
import { useSessionStore } from '../session/store/session.store';
import DsrNamingSubContainer from './DsrNamingSubContainer.vue';
import { useLoadTemplates } from './composables/load-templates.composable';
import TemplateBreadcrumbs from './TemplateBreadcrumbs.vue';
import LastUsedCard from './LastUsedCard.vue';
import TemplateTable from './TemplateTable.vue';
import DsrGlobalTemplateBanner from './DsrGlobalTemplateBanner.vue';

export default defineComponent({
  name: 'DsrTemplatePicker',
  components: {
    TemplateTable,
    TemplateBreadcrumbs,
    SearchContent,
    LastUsedCard,
    DsrNamingSubContainer,
    DsrGlobalTemplateBanner,
  },
  setup(_, { emit }) {
    const {
      loadLastUsed,
      loadByDirectory,
      loadMoreResources,
      searchTemplates,
      hasNextPage,
      lastUsedTemplates,
      loading,
      error,
      currentDirectory,
      templates,
      directories,
      searching,
      setSort,
      sortOrder,
      sortBy,
    } = useLoadTemplates();

    const { showAdmin: isAdmin } = useSessionStore();
    const latestSearch = ref('');
    const showNamingModal = ref(false);
    const templateName = ref('');
    const templateId = ref('');
    const creating = ref(false);
    const showGlobalTemplateBanner = ref(false);

    const createRoom = ({ name, companyName }: CreateRoomPayload) => {
      if (templateId.value) {
        useTemplate({ id: templateId.value, name, companyName });
        return;
      }

      createDsr({ name, companyName });
    };

    const createDsr = async ({ name, companyName }: CreateRoomPayload) => {
      creating.value = true;
      try {
        const id = await DsrService.createDealroom({
          name,
          companyName,
        });
        emit('dsr-created', { id, type: EnumDocumentType.DSR });
      } catch (e) {
        console.error('Could not create dsr', e);
      } finally {
        creating.value = false;
      }
    };

    const selectTemplate = async ({ id, name }: { id: string; name: string }) => {
      showNamingModal.value = true;
      templateId.value = id;
      templateName.value = name;
    };

    const resetNamingModal = () => {
      latestSearch.value = '';
      currentDirectory.value = DsrDirectoryConstants.RootPath;
      loadByDirectory(currentDirectory.value);
      showNamingModal.value = false;
      templateId.value = '';
      templateName.value = '';
    };

    const useTemplate = async (payload: CreateRoomFromTemplatePayload) => {
      creating.value = true;

      try {
        const dsrId = await DsrService.useTemplate(payload);
        emit('dsr-created', { id: dsrId, type: EnumDocumentType.DSR });
      } catch (e) {
        console.error('Could not use template', e);
      } finally {
        creating.value = false;
      }
    };

    const handleSort = (sort: { sortBy: DsrSortAttribute; sortOrder: SortOrder }) => {
      setSort(sort.sortBy, sort.sortOrder);
      handleSearch(latestSearch.value);
    };

    const handleSearch = (searchTerm: string) => {
      latestSearch.value = searchTerm;

      if (searchTerm) {
        searchTemplates(searchTerm);
      } else {
        loadByDirectory(currentDirectory.value);
      }
    };

    const templatePicker = ref<HTMLDivElement | null>(null);

    onMounted(() => {
      loadLastUsed();
      loadByDirectory();
      showGlobalTemplateBanner.value =
        localStorage.getItem(DsrTemplatePickerConstants.globalTemplateBannerLocalStorageKey) !==
        'false';
    });

    return {
      ModalSize,
      creating,
      loading,
      error,
      createDsr,
      useTemplate,
      sortOrder,
      sortBy,
      templates,
      directories,
      loadByDirectory,
      loadMoreResources,
      hasNextPage,
      currentDirectory,
      DsrDirectoryConstants,
      searching,
      searchTemplates,
      handleSearch,
      lastUsedTemplates,
      handleSort,
      ButtonType,
      ParagraphSize,
      showNamingModal,
      templateName,
      selectTemplate,
      resetNamingModal,
      createRoom,
      latestSearch,
      templatePicker,
      showGlobalTemplateBanner,
      isAdmin,
      templateId,
    };
  },
});
